import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  ILabelResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/ILabelResponseInstance'
import {
  ICreateLabelRequestInterfaces,
} from '@toolify/server/src/controllers/LabelController/validators/CreateLabel/types/ICreateLabelRequestInterfaces'
import {
  IGetLabelsRequestInterfaces,
} from '@toolify/server/src/controllers/LabelController/validators/GetLabels/types/IGetLabelsRequestInterfaces'
import {
  IGetIsLabelInUseRequestInterfaces,
} from '@toolify/server/src/controllers/LabelController/validators/GetDatabaseItemsUsingLabels/types/IGetIsLabelInUseRequestInterfaces'
import {IUpdateLabelRequestInterfaces} from '@toolify/server/src/controllers/LabelController/validators/UpdateLabel/types/IUpdateLabelRequestInterfaces'
import {IDeleteLabelRequestInterfaces} from '@toolify/server/src/controllers/LabelController/validators/DeleteLabel/types/IDeleteLabelRequestInterfaces'
import {createKeyedRequestCache} from '../keyedRequestCache'

const LABELS_CACHE_TTL_MS = 60 * 1000
const labelsCacheByPropertyId = createKeyedRequestCache<ILabelResponseInstance[]>({ttlMs: LABELS_CACHE_TTL_MS})

export const useLabelApiStore = defineStore('labelApi', {
  actions: {
    async getLabels(databasePropertyId: string): Promise<ILabelResponseInstance[]> {
      return labelsCacheByPropertyId.get(databasePropertyId, async () => {
        const apiRequestStore = useApiRequestStore()
        const {labels} = await apiRequestStore.get<IGetLabelsRequestInterfaces>(`/property/${databasePropertyId}/labels`)
        return labels
      })
    },

    async getIsLabelInUse(databasePropertyId: string, labelId: string): Promise<boolean> {
      const apiRequestStore = useApiRequestStore()
      const {isLabelInUse} = await apiRequestStore.get<IGetIsLabelInUseRequestInterfaces>(`/property/${databasePropertyId}/label/${labelId}`)
      return isLabelInUse
    },

    async createLabel(databasePropertyId: string, data: unknown): Promise<{label: ILabelResponseInstance}> {
      const apiRequestStore = useApiRequestStore()
      const {label} = await apiRequestStore.post<ICreateLabelRequestInterfaces>(`/property/${databasePropertyId}/label`, {
        data,
      })
      labelsCacheByPropertyId.invalidate(databasePropertyId)
      return {
        label,
      }
    },

    async deleteLabel(labelId: unknown): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.delete<IDeleteLabelRequestInterfaces>(`/label/${labelId}`)
      labelsCacheByPropertyId.invalidateAll()
    },

    async updateLabel(labelId: unknown, title: string, color:string, position?: number): Promise<{label: ILabelResponseInstance}> {
      const apiRequestStore = useApiRequestStore()
      const {label} = await apiRequestStore.patch<IUpdateLabelRequestInterfaces>(`/label/${labelId}`, {
        data: {
          title,
          color,
          position,
        },
      })
      labelsCacheByPropertyId.invalidateAll()
      return {
        label,
      }
    },

    invalidateLabelsCache(databasePropertyId?: string): void {
      if(databasePropertyId) {
        labelsCacheByPropertyId.invalidate(databasePropertyId)
        return
      }
      labelsCacheByPropertyId.invalidateAll()
    },
  },
})
